import React, {useState} from "react";
import {Field, getIn} from "formik";
import map from "lodash/map";
import upperFirst from "lodash/upperFirst";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";

const fieldToTextField = ({
  disabled,
  field: { onBlur: fieldOnBlur, ...field },
  form: { isSubmitting, touched, errors, submitCount },
  onBlur,
  helperText,
  ...props
}) => {
  const fieldError = getIn(errors, field.name);
  const showError = (getIn(touched, field.name) || submitCount > 0) && !!fieldError;

  return {
    error: showError,
    helperText: showError ? fieldError : helperText,
    disabled: disabled ?? isSubmitting,
    onBlur: onBlur ?? (event => fieldOnBlur(event ?? field.name)),
    ...field,
    ...props,
  };
};

const FormikAutocomplete = ({
  textFieldProps,
  saveCustomOption,
  showNAButton = true,
  popperPlacement,
  listboxMaxHeight = "30vh",
  endAdornment,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const {
    form: { setTouched, setFieldValue },
  } = props;
  const options = map(props.options || [], option => ({
    ...option,
    label: option.label || "No Label",
  }));
  const { error, helperText, ...field } = fieldToTextField(props);
  const { name } = field;

  const onOptionSelected = option => {
    if (typeof option === "object" && option !== null) {
      return option.label || "";
    }

    const selected = options.find(v => v.value === option);
    if (selected) return selected.label;

    return typeof option === "string" ? option : "";
  };

  // MUI keys each listbox row by its label unless told otherwise. Distinct
  // options can share a label (e.g. two projects with the same name), and the
  // duplicate React keys leave stale rows behind when typing filters the list.
  // Key by the option's value instead. Returning undefined makes MUI fall back
  // to the label key for options without a usable primitive value.
  const defaultGetOptionKey = option => {
    const value = option?.value;
    if (typeof value === "string" || typeof value === "number") return value;
    return undefined;
  };

  const isEmpty = !field.value || field.value === "";
  const showNABtn = showNAButton && isFocused && isEmpty;

  const handleNAClick = () => {
    setFieldValue(name, "N/A");
    setOpen(false);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setIsFocused(false);
      }}
      {...props}
      {...field}
      id={props.id ? props.id : `field-${name}`}
      data-id={`select${upperFirst(name)}`}
      onChange={(e, value) => {
        setFieldValue(name, value ? value.value : "");
        if (props.onChange) props.onChange(value);
      }}
      options={options}
      value={field.value || ""}
      isOptionEqualToValue={(option, value) => {
        if (!value) return false;
        return option.value === value;
      }}
      getOptionLabel={onOptionSelected}
      getOptionKey={props.getOptionKey ?? defaultGetOptionKey}
      onBlur={() => setTouched({ [name]: true })}
      {...(popperPlacement && {
        slotProps: {
          popper: {
            placement: popperPlacement,
            modifiers: [{ name: "flip", enabled: false }],
          },
          listbox: {
            sx: { maxHeight: listboxMaxHeight },
          },
        },
      })}
      renderInput={inputProps => (
        <TextField
          {...inputProps}
          size="small"
          name={name}
          helperText={helperText}
          error={error}
          label={props.label}
          placeholder={props.placeholder}
          variant="outlined"
          margin="none"
          {...textFieldProps}
          InputProps={{
            ...inputProps.InputProps,
            endAdornment: (
              <>
                {showNABtn && (
                  <InputAdornment position="end">
                    <Chip
                      label="N/A"
                      size="small"
                      clickable
                      color="primary"
                      variant="outlined"
                      onMouseDown={handleNAClick}
                      sx={{ cursor: "pointer", mr: 1 }}
                    />
                  </InputAdornment>
                )}
                {endAdornment}
                {inputProps.InputProps.endAdornment}
              </>
            ),
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={
            saveCustomOption
              ? value => {
                  setFieldValue(field.name, value.target.value);
                  setIsFocused(false);
                }
              : () => setIsFocused(false)
          }
          onKeyDown={e => {
            if (e.key === "Enter") {
              setOpen(true);
            }
          }}
        />
      )}
    />
  );
};

export default props => {
  return (
    <FormControl fullWidth>
      <Field
        component={FormikAutocomplete}
        textFieldProps={{
          fullWidth: true,
          margin: "none",
          variant: "outlined",
        }}
        {...props}
      />
    </FormControl>
  );
};
